import { useState, useEffect, useCallback } from "react";
import { LessonRateModel } from "app/models/scheduling/LessonRateModel";
import {
  getFacilityLessonRates,
  getPublicFacilityLessonRates,
} from "app/utils/request/scheduling/facilityLessonRates";

export interface UseFacilityLessonRatesResult {
  rates: LessonRateModel[];
  loading: boolean;
  error: boolean;
}

export default function useFacilityLessonRates(
  facilityId: string | undefined,
  enabled: boolean,
  isPublic = false
): UseFacilityLessonRatesResult {
  const [rates, setRates] = useState<LessonRateModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const fetchRates = useCallback(() => {
    if (!enabled || !facilityId) {
      setRates([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(false);

    const fetchFacilityRates = isPublic
      ? getPublicFacilityLessonRates
      : getFacilityLessonRates;

    fetchFacilityRates(facilityId)
      .then((fetchedRates) => {
        const activeRates = fetchedRates.filter(
          (rate) => rate.active !== false
        );
        activeRates.sort((a, b) => {
          const orderA = a.orderNumber ?? Number.MAX_SAFE_INTEGER;
          const orderB = b.orderNumber ?? Number.MAX_SAFE_INTEGER;
          return orderA - orderB;
        });
        setRates(activeRates);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching facility lesson rates:", err);
        setError(true);
        setLoading(false);
      });
  }, [facilityId, enabled, isPublic]);

  useEffect(() => {
    fetchRates();
  }, [fetchRates]);

  return { rates, loading, error };
}
